<template>
  <!--
    Golem fork (FT-1384): THE NIGHT MARKS — each acting character's own dress
    on the coins while the night is asking, staged, and sealed.

    THREE STATES, one word each, decided entirely by Player.vue
    (nightMarkState) so this file is a RENDERER and never a second rules
    engine:

      invite   state 1 — the night wants a pick and this coin can take it.
               The one treatment allowed to LOOP (the breathe), because an
               invitation that stops moving stops inviting.
      staged   state 2 — the pick has settled here, still movable. The mark
               arrives in one short motion (≤1s) and then HOLDS; nothing
               loops, because a staged pick is a decision at rest.
      sealed   state 3 — the Confirm was pressed (or the storyteller's answer
               landed). The role's seal plays ONCE (≤1s) and holds until the
               storyteller re-asks. This is the receipt now — the FT-1330
               receive-only sentences stood down in its favour.

    THE ART REGISTER is the 2026-09-04 concept strips
    (claude_temp_test/2026-09-04-nightstates-shots/concept-*.png): dark
    bodies, one hot rim light, organic edges. Colours are each role's own —
    the Monk's white-gold, the Poisoner's sick green — never the team blues
    and reds (a night mark must not say what anyone IS; the FT-1150 rule).

    A ROLE WITHOUT AUTHORED ART RENDERS NOTHING — the purple ring idiom in
    Player.vue still dresses every un-authored character exactly as before
    this card. Roles land here one commit at a time (Monk first, the
    reference); which roles have landed is the night signs registry's fact
    now (golem/nightSigns — FT-1403 phase 1 moved the old HAS_ART there).

    GEOMETRY: one SVG filling the coin's round box, with headroom above it
    (the viewBox starts at -20) because several marks live at the coin's
    shoulder — the Monk's halo hovers ABOVE the rim before it lands.
    pointer-events are off on everything: the mark never intercepts the tap
    it is decorating.

    FT-1386 (user-vetted): every FULL-CIRCLE mark that circles a character
    — beads, seep rings, ember ticks, the seal rings — draws OUTSIDE the
    coin's rim (r=55 on the rim's 50, spilling past the viewBox onto the
    dark cloth; overflow is visible the whole way up, nothing clips).
    Accents that sit ON the face (slashes, bubbles, drips, eyes, halos,
    pegs, tacks, seals, badges, the pentagram) stay where they are.
  -->
  <!--
    FT-1385 adds the TOLD-INFORMATION states beside the three above. The
    told roles (washerwoman, librarian, investigator, chef, empath) choose
    nothing — they are TOLD — so their grammar has two beats, not three:

      telling  the storyteller's Send landed: the information arrives in
               the role's own language. One arrival motion (≤1s), then a
               bright held pose.
      settled  the residue: effects expire, knowledge does not. The dress
               eases to a quiet dotted trace that persists all game.

    `part` says which coin this mark stands on: "target" (a coin the
    information points at — the candidate pair, the Empath's neighbours)
    or "self" (the told player's own chair, the residue's home).
  -->
  <span
    v-if="hasArt"
    class="nm"
    :class="['nm-' + state, 'nm-' + roleId, part ? 'nm-part-' + part : '']"
    aria-hidden="true"
  >
    <!-- ── MONK — THE HALO (protection descends) ─────────────────────────
         invite: a rosary-bead ring breathes on every legal coin.
         staged: the halo hovers over the picked coin, rays waking.
         sealed: the halo DROPS onto the coin and rings once like a struck
                 bell, then holds as a solid ring + a soft dome of light. -->
    <svg v-if="roleId === 'monk'" viewBox="0 -24 100 124">
      <template v-if="state === 'invite'">
        <!-- r=55: OUTSIDE the coin's rim (FT-1386, user-vetted). A ring
             that circles a character sits on the dark table cloth, not on
             the face — the old inside placement (r=38) was ducking the
             brass gear teeth at the rim, and out here there is no brass to
             vanish against. -->
        <circle class="mk-beads" cx="50" cy="50" r="55" />
      </template>
      <template v-else-if="state === 'staged'">
        <g class="mk-hover">
          <ellipse class="mk-halo" cx="50" cy="-8" rx="21" ry="6" />
          <path class="mk-ray" d="M50 -20 l0 -5" />
          <path class="mk-ray mk-ray2" d="M32 -14 l-4 -3" />
          <path class="mk-ray mk-ray3" d="M68 -14 l4 -3" />
        </g>
      </template>
      <template v-else-if="state === 'sealed'">
        <!-- the bell-flash: one expanding ring that burns out -->
        <circle class="mk-toll" cx="50" cy="50" r="55" />
        <!-- the dome of light, held -->
        <path
          class="mk-dome"
          d="M 8 50 A 42 42 0 0 1 92 50 L 86 50 A 36 36 0 0 0 14 50 Z"
        />
        <!-- the halo, landed on the rim -->
        <ellipse class="mk-halo mk-landed" cx="50" cy="2" rx="19" ry="5.5" />
        <!-- the solid ring the seal holds as — outside the rim (FT-1386) -->
        <circle class="mk-seal-ring" cx="50" cy="50" r="55" />
      </template>
    </svg>

    <!-- ── POISONER — THE MIASMA (venom haunts the coin) ─────────────────
         invite: a sick-green seep ring breathes on every legal coin, one
                 bright bead slowly circling it (the state-1 loop).
         staged: a sick-green glow wakes BEHIND the chosen coin — the v3
                 miasma's behind-coin aura, foot fog and motes dropped
                 (FT-1395, user-picked). One fade-in (≤1s), then it HOLDS.
         sealed: one DEEPER-green circle closes round the coin in a single
                 sweep, outside the rim (FT-1386), and goes STILL; the
                 staged glow stays underneath, dimmed. -->
    <svg v-else-if="roleId === 'poisoner'" viewBox="0 -24 100 124">
      <template v-if="state === 'invite'">
        <circle class="ps-seep-ring" cx="50" cy="50" r="55" />
        <circle class="ps-bead" cx="50" cy="50" r="55" pathLength="1" />
      </template>
      <template v-else-if="state === 'staged'">
        <!-- the aura hugs the rim (stroke centred just inside it) so the
             blur leaks out past the coin's edge — light seen from BEHIND
             the coin, never a wash over its face -->
        <circle class="ps-aura" cx="50" cy="50" r="53" />
        <path class="ps-aura-low" d="M 6.3 80 A 53 53 0 0 0 93.7 80" />
      </template>
      <template v-else-if="state === 'sealed'">
        <circle class="ps-aura ps-aura-dim" cx="50" cy="50" r="53" />
        <circle class="ps-seal-ring" cx="50" cy="50" r="55" pathLength="1" />
      </template>
    </svg>

    <!-- ── FORTUNE TELLER — THE SCRYING THREAD (two coins, one question) ──
         invite: sparkles wink in and out around every legal coin.
         staged: a starlight crescent veils each picked coin; the dotted
                 threads from her OWN coin to each pick are NightThread.vue's
                 (FT-1388 — they live between coins, not on one).
         sealed: the eye opens on the coin and stays open; the threads burn
                 solid one component over. -->
    <svg v-else-if="roleId === 'fortuneteller'" viewBox="0 -24 100 124">
      <template v-if="state === 'invite'">
        <path class="ft-spark ft-s1" :d="star(15, 26, 3.2)" />
        <path class="ft-spark ft-s2" :d="star(84, 20, 2.6)" />
        <path class="ft-spark ft-s3" :d="star(74, 82, 3)" />
        <path class="ft-spark ft-s4" :d="star(22, 76, 2.2)" />
      </template>
      <template v-else-if="state === 'staged'">
        <!-- a lune hugging the coin's upper-left rim: the outer arc rides
             the coin's own circle (r≈44 on c 50,50), the inner arc cuts
             back shallower (r=33), so the veil is thickest at its middle —
             a real crescent, not a hairline (the first draw measured
             invisible under the staged ring). -->
        <path
          class="ft-crescent"
          d="M 19 19 A 44 44 0 0 0 10 63 A 33 33 0 0 1 19 19 Z"
        />
        <path class="ft-spark ft-held ft-s1" :d="star(20, 14, 2.8)" />
        <path class="ft-spark ft-held ft-s3" :d="star(9, 68, 2.2)" />
      </template>
      <template v-else-if="state === 'sealed'">
        <g class="ft-eye">
          <path class="ft-lid" d="M 32 8 Q 50 -6 68 8 Q 50 22 32 8 Z" />
          <circle class="ft-iris" cx="50" cy="8" r="4.6" />
          <circle class="ft-pupil" cx="50" cy="8" r="2" />
        </g>
      </template>
    </svg>

    <!-- ── BUTLER — THE CORD (deference, tied off) ───────────────────────
         invite: a loose ribbon bow sways at every legal coin's shoulder.
         staged: the bow waits, loose-knotted, on the master's coin — the
                 slack cord running from the Butler's own chair is
                 NightThread.vue's half of the act.
         sealed: the cord snaps taut and the bow CINCHES shut here, with one
                 sparkle. -->
    <svg v-else-if="roleId === 'butler'" viewBox="0 -24 100 124">
      <template v-if="state === 'invite'">
        <g class="bt-bow bt-sway">
          <path
            class="bt-loop"
            d="M 78 22 C 70 10, 58 15, 65 24 C 69 29, 74 26, 78 22 Z"
          />
          <path
            class="bt-loop"
            d="M 78 22 C 86 10, 98 15, 91 24 C 87 29, 82 26, 78 22 Z"
          />
          <path class="bt-tail" d="M 76 24 q -2 8 -6 11" />
          <path class="bt-tail" d="M 80 24 q 2 8 6 11" />
          <circle class="bt-knot" cx="78" cy="22" r="2.2" />
        </g>
      </template>
      <template v-else-if="state === 'staged'">
        <g class="bt-bow bt-loose">
          <path
            class="bt-loop"
            d="M 78 22 C 70 10, 58 15, 65 24 C 69 29, 74 26, 78 22 Z"
          />
          <path
            class="bt-loop"
            d="M 78 22 C 86 10, 98 15, 91 24 C 87 29, 82 26, 78 22 Z"
          />
          <path class="bt-tail" d="M 76 24 q -2 8 -6 11" />
          <path class="bt-tail" d="M 80 24 q 2 8 6 11" />
          <circle class="bt-knot" cx="78" cy="22" r="2.2" />
        </g>
      </template>
      <template v-else-if="state === 'sealed'">
        <g class="bt-bow bt-cinched">
          <path
            class="bt-loop"
            d="M 78 22 C 72 13, 63 17, 68 24 C 71 28, 75 25, 78 22 Z"
          />
          <path
            class="bt-loop"
            d="M 78 22 C 84 13, 93 17, 88 24 C 85 28, 81 25, 78 22 Z"
          />
          <path class="bt-tail" d="M 76 24 q -1.5 6 -4.5 8" />
          <path class="bt-tail" d="M 80 24 q 1.5 6 4.5 8" />
          <circle class="bt-knot" cx="78" cy="22" r="2.4" />
        </g>
        <path class="bt-spark" :d="star(90, 10, 3.4)" />
      </template>
    </svg>

    <!-- ── IMP — THE RUNE PENTAGRAM (the kill is signed) ─────────────────
         invite: a RED DOTTED border rings every live coin — the Imp's OWN
                 coin included, deliberately: that door is the starpass.
                 (FT-1384 v2: replaced the flickering ember ticks — one
                 arrival, then held still. v3: hovering a legal coin melts
                 the dots SOLID — the mouse-only "this one?" affordance.)
         staged: the PENTAGRAM lands on the picked coin inside ONE full
                 solid RED circle (v4, user-vetted: "the outline should be
                 all red and a full circle" — the open ember arc retired
                 below, and the generic purple pick ring stands down in
                 Player.vue's imp-staged rule).
         sealed: the circle HOLDS — in the app's own sealed/locked PURPLE
                 (v5, user: "the border on the sealed can be purple") — and
                 the flare rings once round the STANDING pentagram; embers
                 drift up and die, following the ring's ink. The RED lives
                 on in the pentagram alone: purple ring over red glyph is
                 the intended contrast. -->
    <svg v-else-if="roleId === 'imp'" viewBox="0 -24 100 124">
      <template v-if="state === 'invite'">
        <!-- the ember ticks, retired 2026-09-05 (kept per never-delete):
        <path
          v-for="(t, i) in EMBER_TICKS"
          :key="'et' + i"
          class="im-tick"
          :class="'im-t' + (i % 4)"
          :d="t"
        />
        -->
        <circle class="im-invite-ring" cx="50" cy="50" r="55" pathLength="1" />
      </template>
      <template v-else-if="state === 'staged'">
        <!-- the open ember arc, retired 2026-09-05 FT-1384 v4 (kept per
             never-delete):
        <circle class="im-rune-arc" cx="50" cy="50" r="55" pathLength="1" />
        -->
        <circle class="im-staged-ring" cx="50" cy="50" r="55" />
        <path
          class="im-penta"
          d="M 50 20 L 67.6 74.3 L 21.5 40.7 L 78.5 40.7 L 32.4 74.3 Z"
        />
        <!-- the claw slashes, retired 2026-09-05 FT-1384 v3 (kept per
             never-delete):
        <path class="im-slash im-s1" d="M 36 18 q 4 22 -2 48" pathLength="1" />
        <path class="im-slash im-s2" d="M 52 14 q 3 24 -1 54" pathLength="1" />
        <path class="im-slash im-s3" d="M 66 18 q 2 20 -2 46" pathLength="1" />
        -->
      </template>
      <template v-else-if="state === 'sealed'">
        <circle class="im-flare" cx="50" cy="50" r="55" />
        <circle class="im-rune-ring" cx="50" cy="50" r="55" pathLength="1" />
        <path
          class="im-penta im-penta-held"
          d="M 50 20 L 67.6 74.3 L 21.5 40.7 L 78.5 40.7 L 32.4 74.3 Z"
        />
        <!-- the sealed claw slashes, retired with the staged ones (FT-1384
             v3, kept per never-delete):
        <path class="im-slash im-hard im-s1" d="M 36 18 q 4 22 -2 48" />
        <path class="im-slash im-hard im-s2" d="M 52 14 q 3 24 -1 54" />
        <path class="im-slash im-hard im-s3" d="M 66 18 q 2 20 -2 46" />
        -->
        <circle class="im-ember im-e1" cx="34" cy="6" r="1.7" />
        <circle class="im-ember im-e2" cx="52" cy="0" r="2" />
        <circle class="im-ember im-e3" cx="66" cy="8" r="1.4" />
        <circle class="im-ember im-e4" cx="44" cy="-6" r="1.4" />
      </template>
    </svg>

    <!-- ── RAVENKEEPER — THE WING WREATH (the ravens choose a roost) ─────
         The dead actor's mark: this ask only exists for a seat that died
         tonight, and the machinery already knows it (deadStillWakes).
         invite: a ring of small BLUE feathers stands round every legal
                 coin — the Imp dotted-border idiom with feathers for the
                 dots (v5, user: "the feathers as the dotted blue border
                 for selection"). One arrival, then STILL — the drifting
                 feathers retired below, kept per never-delete.
         staged: the feathers flutter DOWN and settle one by one on the
                 chosen rim, staggered, then rest.
         sealed: the wings FOLD shut around the coin — both sides sweep up
                 and lock — and the moon-eye glints once. -->
    <svg v-else-if="roleId === 'ravenkeeper'" viewBox="0 -24 100 124">
      <template v-if="state === 'invite'">
        <!-- the drifting feathers, retired 2026-09-05 FT-1384 v5 (kept per
             never-delete):
        <g class="rk-drift rk-f1">
          <path class="rk-feather" :d="feather(16, 18)" />
        </g>
        <g class="rk-drift rk-f2">
          <path class="rk-feather" :d="feather(84, 10)" />
        </g>
        <g class="rk-drift rk-f3">
          <path class="rk-feather" :d="feather(70, 78)" />
        </g>
        -->
        <g
          v-for="(t, i) in FEATHER_RING"
          :key="'rkr' + i"
          class="rk-invf"
          :transform="t"
        >
          <path
            class="rk-invf-feather"
            d="M 0 0 q 3.4 -6 1.6 -14 q -5.4 6.4 -1.6 14 Z M 0 0 l 1 -13"
          />
        </g>
      </template>
      <template v-else-if="state === 'staged'">
        <g class="rk-settle rk-f1">
          <path class="rk-feather" :d="feather(20, 26)" />
        </g>
        <g class="rk-settle rk-f2">
          <path class="rk-feather" :d="feather(80, 26)" />
        </g>
        <g class="rk-settle rk-f3">
          <path class="rk-feather" :d="feather(32, 84)" />
        </g>
        <g class="rk-settle rk-f4">
          <path class="rk-feather" :d="feather(68, 84)" />
        </g>
      </template>
      <template v-else-if="state === 'sealed'">
        <g class="rk-wing rk-wl">
          <path class="rk-pleat" d="M 46 92 Q 12 84 6 50" />
          <path class="rk-pleat" d="M 46 92 Q 18 84 14 60" />
          <path class="rk-pleat" d="M 46 92 Q 24 84 22 68" />
        </g>
        <g class="rk-wing rk-wr">
          <path class="rk-pleat" d="M 54 92 Q 88 84 94 50" />
          <path class="rk-pleat" d="M 54 92 Q 82 84 86 60" />
          <path class="rk-pleat" d="M 54 92 Q 76 84 78 68" />
        </g>
        <path
          class="rk-moon"
          d="M 50 -9 A 8.5 8.5 0 1 0 50 8 A 11 11 0 0 1 50 -9 Z"
        />
        <path class="rk-glint" :d="star(58, -6, 3.4)" />
      </template>
    </svg>

    <!-- ══ THE TOLD-INFORMATION MARKS (FT-1385) ═══════════════════════════
         Two states each (telling / settled), two parts (target / self).
         The RING is every target coin's shared grammar — solid and lit at
         the telling, dotted at rest — so "the information points HERE" is
         one shape in five inks; the role's own object (peg, page-corner,
         tack, heart, scorch) is what tells the five apart. -->

    <!-- ── WASHERWOMAN — THE LAUNDRY LINE'S PEGS ──────────────────────────
         telling: the ring strings itself round each candidate coin and a
                  clothespin drops onto the crown with a wooden click; soap
                  bubbles rise once and pop.
         settled: the ring eases to a dotted thread, the bubbles are gone,
                  the peg STAYS clamped on the rim. Self: a small peg rests
                  at the teller's own shoulder — the knowledge kept. -->
    <svg v-else-if="roleId === 'washerwoman'" viewBox="0 -24 100 124">
      <template v-if="part === 'target'">
        <circle class="ww-ring" cx="50" cy="50" r="55" pathLength="1" />
        <g class="ww-peg">
          <path
            d="M 46.5 6 L 46.5 -8 A 3.5 3.5 0 0 1 53.5 -8 L 53.5 6
               L 51.8 6 L 51.8 -2 L 48.2 -2 L 48.2 6 Z"
          />
          <circle class="ww-spring" cx="50" cy="-5" r="1.3" />
        </g>
        <template v-if="state === 'telling'">
          <circle class="ww-bubble ww-b1" cx="38" cy="14" r="2.4" />
          <circle class="ww-bubble ww-b2" cx="58" cy="9" r="1.8" />
          <circle class="ww-bubble ww-b3" cx="66" cy="18" r="1.4" />
        </template>
      </template>
      <template v-else>
        <g class="ww-peg ww-peg-self">
          <path
            d="M 46.5 6 L 46.5 -8 A 3.5 3.5 0 0 1 53.5 -8 L 53.5 6
               L 51.8 6 L 51.8 -2 L 48.2 -2 L 48.2 6 Z"
          />
          <circle class="ww-spring" cx="50" cy="-5" r="1.3" />
        </g>
      </template>
    </svg>

    <!-- ── LIBRARIAN — THE BOOKMARK'S RIBBON TABS ─────────────────────────
         telling: the sepia ring reads itself round each candidate and a
                  blue ribbon tab drops over the coin's crown — this seat is
                  a page the book has marked.
         settled: the ring rests dotted, the tab stays draped. Self: the
                  blue bookmark ribbon hangs from the teller's own rim — the
                  book closed, the place kept. (The zero-Outsiders night
                  strings no coins at all; the empty book is the centre's.)
         FT-1400 vet (user): the candidate mark IS the bookmark ribbon —
         the same notched glyph the teller's own coin hangs ("this cool
         bookmark icon"), told-blue, crown-borne like the peg and the tack.
         The page-corner triangle it replaces is retired below (CSS kept,
         commented, per never-delete). -->
    <svg v-else-if="roleId === 'librarian'" viewBox="0 -24 100 124">
      <template v-if="part === 'target'">
        <circle class="lb-ring" cx="50" cy="50" r="55" pathLength="1" />
        <g class="lb-tab">
          <path d="M 46.5 -8 L 53.5 -8 L 53.5 16 L 50 10.5 L 46.5 16 Z" />
        </g>
      </template>
      <template v-else>
        <g class="lb-ribbon">
          <path d="M 46.5 -8 L 53.5 -8 L 53.5 16 L 50 10.5 L 46.5 16 Z" />
        </g>
        <!-- FT-1400: THE THING ITSELF — the told character's token above
             the bookmark: the book closed on WHO it was about, kept with
             the place. The zero-Outsiders night delivers no character, so
             toldIcon is empty and the blank book's ribbon stays alone. -->
        <g class="lb-token" v-if="toldIcon">
          <circle class="lb-token-disc" cx="50" cy="-19" r="12" />
          <image :href="toldIcon" x="39.5" y="-29.5" width="21" height="21" />
        </g>
      </template>
    </svg>

    <!-- ── INVESTIGATOR — THE EVIDENCE TACKS ──────────────────────────────
         telling: a tack PINS each candidate coin — one hard press — and
                  the blue ring snaps round it; the string one component
                  over snaps taut in the same breath.
         settled: nothing sweetens. The ring dries to dotted, the tack
                  HOLDS. Self: the case's wax seal cools on the teller's
                  own rim — the dossier stamped, kept all game. -->
    <svg v-else-if="roleId === 'investigator'" viewBox="0 -24 100 124">
      <template v-if="part === 'target'">
        <circle class="iv-ring" cx="50" cy="50" r="55" pathLength="1" />
        <g class="iv-tack">
          <path class="iv-pin" d="M 50 -1 L 50 7" />
          <circle class="iv-head" cx="50" cy="-5" r="4.6" />
          <circle class="iv-glint" cx="48.4" cy="-6.6" r="1.2" />
        </g>
      </template>
      <template v-else>
        <g class="iv-seal">
          <path
            d="M 50 -7 c 5 -3.4 9.6 -0.6 9.9 3.4 c 0.4 4.4 -3.4 6.4 -5.4 6.9
               c 2 1.5 1 3.4 -1.2 3.2 c -2.4 -0.2 -7.6 0.6 -9.6 -1.4
               c -2.8 -2.8 -3 -7.6 -0.4 -10.1 c 2 -1.9 4.4 -2.4 6.7 -2 z"
          />
          <circle class="iv-boss" cx="50" cy="-1" r="3.4" />
        </g>
      </template>
    </svg>

    <!-- ── CHEF — THE SCORCH BADGE ────────────────────────────────────────
         The count points at nobody the Chef can see into, so the whole
         dress lives on her OWN coin (part self; toldSeats returns no
         targets) and in the centre, where the numeral takes the brand.
         telling: the scorch ring SEARS on around the pair-mark — two
                  touching coins, the thing the number counts — with ember
                  flecks lifting once.
         settled: the flames die first; the brand cools to a dotted scorch
                  and stays as the badge, permanent. -->
    <svg v-else-if="roleId === 'chef'" viewBox="0 -24 100 124">
      <g class="ch-badge">
        <circle class="ch-scorch" cx="50" cy="-4" r="12.5" pathLength="1" />
        <!-- FT-1400 vet (user): the badge SAYS the number — the told count
             at the scorch's centre, the Empath count-badge idiom ("0" is
             real knowledge and renders; a null night delivers no numeral
             and keeps the bare pair-mark below). -->
        <!-- dy: PiratesBay's digit ink rides ~0.095em low in its em box
             (the same bias the centre chip corrects in NightCall), so the
             central baseline alone leaves the count visibly sunk. -->
        <text v-if="toldNumeral" class="ch-count-num" x="50" y="-4" dy="-0.1em">
          {{ toldNumeral }}
        </text>
        <g class="ch-pair" v-else>
          <circle cx="45.6" cy="-4" r="4.1" />
          <circle cx="54.4" cy="-4" r="4.1" />
        </g>
      </g>
      <template v-if="state === 'telling'">
        <circle class="ch-fleck ch-f1" cx="42" cy="-14" r="1.5" />
        <circle class="ch-fleck ch-f2" cx="51" cy="-18" r="1.8" />
        <circle class="ch-fleck ch-f3" cx="59" cy="-13" r="1.2" />
      </template>
    </svg>

    <!-- ── EMPATH — THE HEART-THREADS' BEADS ──────────────────────────────
         telling: a vein pulses out to each live neighbour (NightThread's
                  half) and a heart bead lands on the neighbour's rim with
                  one heartbeat; the pink ring names the coin.
         settled: the ring rests dotted, the bead stays faint — and the
                  whole dress re-runs the same beat next night on the same
                  threads (a new row re-tells; a dead neighbour re-anchors
                  the vein and its bead by itself). Self: a small heart at
                  her own shoulder — what she knows, kept. -->
    <template v-else-if="roleId === 'empath'">
      <svg viewBox="0 -24 100 124">
        <template v-if="part === 'target'">
          <circle class="em-ring" cx="50" cy="50" r="55" pathLength="1" />
          <path
            class="em-heart"
            d="M 50 2 C 42.5 -5.5 44 -13.5 50 -9.5 C 56 -13.5 57.5 -5.5 50 2 Z"
          />
        </template>
        <template v-else>
          <!-- FT-1401 v2 (user): the standing shoulder heart YIELDS to the
               ledger — one o'clock is its post and night 1's mark takes it
               (the user's call: the ledger REPLACES the heart as the
               sequence), so it stands only while no ledger row does. -->
          <path
            v-if="!ledger.length"
            class="em-heart em-heart-self"
            d="M 50 2 C 42.5 -5.5 44 -13.5 50 -9.5 C 56 -13.5 57.5 -5.5 50 2 Z"
          />
          <!-- FT-1401: the FT-1400 single count badge (em-count) RETIRED —
               the nightly ledger below keeps every night's figure now, so
               one badge holding only the latest said less than the coin
               already knows. CSS kept per never-delete; restore this group
               to bring the single badge back:
          <g class="em-count" v-if="toldNumeral">
            <circle class="em-count-ring" cx="67" cy="-4" r="9" />
            <text class="em-count-num" x="67" y="-4" dy="-0.1em">
              {{ toldNumeral }}
            </text>
          </g>
          -->
        </template>
      </svg>
      <!-- FT-1401: THE NIGHTLY LEDGER — every night's reading, kept AROUND
           her own coin in wake order: a small heart for a reading of 0, a
           blood-red ringed numeral for 1 or 2 (red is the user's explicit
           pick for this element — the FT-1392 town-blue rule is
           user-exempted here). FT-1401 v2 (user, from the live shots):
           CLOCKWISE from ONE O'CLOCK, 30° per night — the clock's own hour
           posts: night 1 at one o'clock, night 2 at two, night 3 at three
           — at r=58, outside the rim (FT-1386). One o'clock is the
           standing shoulder heart's own post, so while any ledger row
           stands the ledger REPLACES that heart as the sequence (the v-if
           above): night 1 takes the post, and a zero night sets a heart
           right back where the shoulder heart stood. Its own SVG because
           the self dress above rides the shoulder transform and a ledger
           that circles the coin cannot (see .em-ledger's override).
           Crowding: 30° per night holds 12 nights; past that the whole arc
           compresses evenly and the marks shrink with the step (ledgerAt).
           Only the Empath's own client ever holds these rows — a bystander
           and the storyteller see nothing new. -->
      <svg
        v-if="part === 'self' && ledger.length"
        class="em-ledger"
        viewBox="0 -24 100 124"
      >
        <g
          v-for="(m, i) in ledger"
          :key="'led' + m.day"
          class="em-led"
          :transform="ledgerAt(i)"
        >
          <!-- FT-1401 v4 (user): r2 heart back. Round 3's grown-copy path
               (coordinates ×1.75, baked into the literal numbers) kept-
               commented per never-delete:
          <path
            v-if="m.number === 0"
            class="em-led-heart"
            d="M 0 6.3 C -7.2 -1.1 -5.8 -8.8 0 -4.9 C 5.8 -8.8 7.2 -1.1 0 6.3 Z"
          />
          -->
          <!-- what stands is round 2's own heart, verbatim "d" — the
               grown FOOTPRINT round 3 established stays via this group's
               own scale(1.75), so the coin keeps its size while the path
               data reads as round 2's. -->
          <g v-if="m.number === 0" transform="scale(1.75)">
            <path
              class="em-led-heart"
              d="M 0 3.6 C -4.1 -0.6 -3.3 -5 0 -2.8 C 3.3 -5 4.1 -0.6 0 3.6 Z"
            />
          </g>
          <!-- FT-1401 v2: the numeral never sits bare — a blood-red ring
               circles it, the framing the heart mark's stroked body gives
               the zero nights (dark ground, red rim). dy: the PiratesBay
               ink-low bias, the same correction the Chef's counted scorch
               and the centre chip carry. -->
          <template v-else>
            <circle class="em-led-ring" cx="0" cy="0" r="7.5" />
            <text class="em-led-num" x="0" y="0" dy="-0.1em">
              {{ m.number }}
            </text>
            <!-- FT-1401 v4 (user): drips retired — red alone. THE BLOOD
                 RUNS (drawn drips falling from the digit's foot) kept-
                 commented per never-delete:
            <g class="em-led-blood" transform="translate(0 1.9)">
              <path
                class="em-led-drip"
                d="M -2.05 -0.8 C -2.15 1 -2.12 2.4 -1.88 3.55
                   C -1.8 3.95 -1.5 3.95 -1.43 3.55
                   C -1.25 2.4 -1.32 1 -1.3 -0.8 Z"
              />
              <circle class="em-led-drop" cx="-1.66" cy="4" r="0.62" />
              <circle class="em-led-dtip" cx="-1.66" cy="4.18" r="0.4" />
              <path
                class="em-led-drip"
                d="M 1.25 -0.8 C 1.18 0.2 1.2 1.15 1.4 2
                   C 1.47 2.32 1.72 2.32 1.78 2
                   C 1.95 1.15 1.9 0.2 1.9 -0.8 Z"
              />
              <circle class="em-led-drop" cx="1.58" cy="2.4" r="0.5" />
              <circle class="em-led-dtip" cx="1.58" cy="2.54" r="0.32" />
            </g>
            -->
          </template>
        </g>
      </svg>
    </template>

    <!-- ── GRANDMOTHER — THE SPECTACLES (FT-1402: she KNOWS this one) ─────
         An identity telling, not a pair: the one grandchild coin wears her
         reading spectacles on its crown — her role art's own glyph, picked
         over a locket for coin-size legibility (two linked lens rings read
         at a glance where a small oval pendant blurs into the token discs).
         telling: the gold ring strings itself round the grandchild and the
                  spectacles settle onto the crown — one drop, then still.
         settled: the ring rests dotted, the SPECTACLES HOLD, all game —
                  her life hangs on that seat, so this is the one told mark
                  whose persistence is the point, not a courtesy (the
                  residue idiom already never expires).
         self: the told character's role token at her own shoulder (the
               told-residue idiom, the Librarian's token one card over) —
               WHO the grandchild is lives on their coin; WHAT they are
               lives on hers. -->
    <svg v-else-if="roleId === 'grandmother'" viewBox="0 -24 100 124">
      <template v-if="part === 'target'">
        <circle class="gm-ring" cx="50" cy="50" r="55" pathLength="1" />
        <g class="gm-specs">
          <circle class="gm-lens" cx="42.6" cy="-5" r="6.2" />
          <circle class="gm-lens" cx="57.4" cy="-5" r="6.2" />
          <path class="gm-wire gm-bridge" d="M 48.2 -6.4 Q 50 -9.2 51.8 -6.4" />
          <path class="gm-wire" d="M 36.4 -6.6 L 31.6 -9.4" />
          <path class="gm-wire" d="M 63.6 -6.6 L 68.4 -9.4" />
        </g>
      </template>
      <template v-else>
        <g class="gm-token" v-if="toldIcon">
          <circle class="gm-token-disc" cx="50" cy="-19" r="12" />
          <image :href="toldIcon" x="39.5" y="-29.5" width="21" height="21" />
        </g>
        <!-- her own folded spectacles rest at the shoulder either way — the
             residue keeps a home mark even when the told character's name
             resolves to no icon (a missing icon is never a broken dress) -->
        <g class="gm-specs gm-specs-self">
          <circle class="gm-lens" cx="42.6" cy="-5" r="6.2" />
          <circle class="gm-lens" cx="57.4" cy="-5" r="6.2" />
          <path class="gm-wire gm-bridge" d="M 48.2 -6.4 Q 50 -9.2 51.8 -6.4" />
        </g>
      </template>
    </svg>

    <!-- ── UNDERTAKER — THE HEADSTONE TOKEN (FT-1412: told, not asked) ────
         A character telling with no seat: the executed player is dead and
         named by daylight already, so no other coin is acknowledged and no
         thread runs — the whole dress lives on his OWN chair (toldSeats
         answers nobody, the Chef's shape; only part "self" ever renders).
         telling: the executed character's token is LOWERED onto the
                  shoulder — one drop into the ground, a small overshoot,
                  then still — and his spade stands beside it, the one
                  graveyard glyph that reads at coin size (his own art).
         settled: token and spade hold, quieter, until the next other-night
                  telling REPLACES them (the store's highest-day rule) or a
                  night with no execution — no character delivered — leaves
                  the coin bare: toldNamed is false and nothing draws.
         The spade stands whenever a character was NAMED, token or not — a
         name that resolves to no icon is never a broken dress. -->
    <svg v-else-if="roleId === 'undertaker'" viewBox="0 -24 100 124">
      <template v-if="part === 'self' && toldNamed">
        <g class="ut-token" v-if="toldIcon">
          <circle class="ut-token-disc" cx="50" cy="-19" r="12" />
          <image :href="toldIcon" x="39.5" y="-29.5" width="21" height="21" />
        </g>
        <!-- the spade, planted point-down beneath the token: a D-handle
             loop, the shaft, the blade — grave-digger's kit, not a team
             colour (the ink is headstone silver, never his art's blue) -->
        <g class="ut-spade">
          <circle class="ut-grip" cx="50" cy="-4.4" r="2.3" />
          <path class="ut-shaft" d="M 50 -2.1 L 50 4.2" />
          <path
            class="ut-blade"
            d="M 44.6 4 L 55.4 4 L 55.4 8.6 Q 50 15.4 44.6 8.6 Z"
          />
        </g>
      </template>
    </svg>
  </span>
</template>

<script>
// FT-1400: the told character resolved to its role, for the bookmark's
// token — content, not state: the STATE stays Player.vue's word, this
// only reads what the standing telling says to draw on the dress.
import { toldCharacterRole } from "../golem/toldInfo";
import { roleIconUrl } from "../golem/roleIcon";
// FT-1403 phase 1: the night signs registry — which roles' art has landed
// (the old in-file HAS_ART and toldInfo's TOLD_ART, asked through one
// module now) and which furniture this role's dress wants (the flags that
// replaced the three role-id guards below).
// enabled() is the FT-1404 "Show night signs" gate — this whole component
// is dress, so the switch stands ALL of it down (the generic purple pick
// idiom in Player.vue keeps dressing the functional states, exactly as it
// always has for an un-authored role).
import {
  enabled,
  hasChooserArt,
  hasToldArt,
  signFlag,
} from "../golem/nightSigns";

/** The told states — the two-beat grammar's own words, disjoint from the
 *  choosing states above so one component can serve both without a role
 *  ever wearing the wrong scaffold. */
const TOLD_STATES = ["telling", "settled"];

/** The Imp's state-1 ember ticks — twelve short radial strokes at r≈55,
 *  just outside the coin's rim (FT-1386), precomputed once (they are the
 *  same on every coin). */
const EMBER_TICKS = (() => {
  const ticks = [];
  for (let i = 0; i < 12; i++) {
    const a = (i / 12) * 2 * Math.PI - Math.PI / 2;
    const x1 = 50 + 52 * Math.cos(a);
    const y1 = 50 + 52 * Math.sin(a);
    const x2 = 50 + 58 * Math.cos(a);
    const y2 = 50 + 58 * Math.sin(a);
    ticks.push(
      `M ${x1.toFixed(1)} ${y1.toFixed(1)} L ${x2.toFixed(1)} ${y2.toFixed(1)}`,
    );
  }
  return ticks;
})();

/** The Ravenkeeper's invite ring (FT-1384 v5) — ten small feathers standing
 *  evenly round r=55 (FT-1386: outside the rim), each rotated to point
 *  outward with its tip on the ring: the Imp dotted-border idiom with
 *  feathers for dots. Precomputed once — the same on every coin. */
const FEATHER_RING = (() => {
  const ring = [];
  for (let i = 0; i < 10; i++) {
    const deg = (i / 10) * 360 - 90;
    const a = (deg * Math.PI) / 180;
    const x = 50 + 55 * Math.cos(a);
    const y = 50 + 55 * Math.sin(a);
    ring.push(
      `translate(${x.toFixed(1)} ${y.toFixed(1)}) ` +
        `rotate(${(deg + 90).toFixed(1)}) scale(0.55)`,
    );
  }
  return ring;
})();

export default {
  name: "NightMark",
  props: {
    roleId: {
      type: String,
      required: true,
    },
    /** "invite" | "staged" | "sealed" — Player.vue's nightMarkState — or a
     *  told state: "telling" | "settled" (FT-1385). */
    state: {
      type: String,
      required: true,
    },
    /** FT-1385, told states only: "target" (a coin the information points
     *  at) or "self" (the told player's own chair). Empty on the choosing
     *  states, which have no parts. */
    part: {
      type: String,
      default: "",
    },
  },
  computed: {
    hasArt() {
      // FT-1404: signs off — no art at all; the viewer's own switch
      if (!enabled()) return false;
      if (TOLD_STATES.includes(this.state)) {
        return hasToldArt(this.roleId);
      }
      return hasChooserArt(this.roleId) && !!this.state;
    },
    EMBER_TICKS() {
      return EMBER_TICKS;
    },
    FEATHER_RING() {
      return FEATHER_RING;
    },
    /** FT-1400: the standing telling — this client's own (night/myTold);
     *  the told marks only ever render on the told player's client, so
     *  the read matches the coin the mark is standing on by construction.
     *  Guarded on roleId anyway: content for THIS role's dress alone. */
    myTelling() {
      if (!TOLD_STATES.includes(this.state)) return null;
      const told = this.$store.getters["night/myTold"];
      return told && told.roleId === this.roleId ? told : null;
    },
    /** FT-1400: the told character's icon for the Librarian's bookmark
     *  token — "" on the zero-Outsiders night (no character delivered)
     *  and for every other role/part, which render exactly as before.
     *  FT-1402: + the Grandmother's own-coin token (the grandchild's
     *  character, worn where her residue lives). */
    toldIcon() {
      if (this.part !== "self") return "";
      // FT-1403 phase 1: the registry's flag, not a role-id list
      if (!signFlag(this.roleId, "wantsCharacterToken")) return "";
      const role = toldCharacterRole(
        this.myTelling,
        this.$store.state.roles,
        this.$store.getters.rolesJSONbyId,
      );
      return role ? roleIconUrl(role, this.$store.getters.rolesJSONbyId) : "";
    },
    /** FT-1412: was a character NAMED at all? The Undertaker's dress hangs
     *  on this rather than on toldIcon — a night with no execution sends
     *  no character and draws nothing (the coin stays bare), while a name
     *  that resolves to no icon still plants the spade. */
    toldNamed() {
      if (this.part !== "self") return false;
      return !!(this.myTelling && this.myTelling.characterName);
    },
    /** FT-1400: the told count for the teller's own coin's badge — the
     *  centre chip dies with the ask, so the residue keeps the numeral.
     *  The Empath's small-badge ring, and (FT-1400 vet) the Chef's scorch
     *  badge, which says the number now instead of the bare pair-mark. */
    toldNumeral() {
      // FT-1403 phase 1: the registry's flag, not a role-id pair
      if (!signFlag(this.roleId, "wantsNumeralBadge")) return "";
      if (this.part !== "self") return "";
      const told = this.myTelling;
      if (!told) return "";
      // a row sent with no number entered stays badge-less — 0 is real
      // knowledge ("no evil neighbours") and renders; null/"" do not
      if (told.number === null || told.number === undefined) return "";
      if (told.number === "") return "";
      const n = Number(told.number);
      return Number.isFinite(n) ? String(n) : "";
    },
    /** FT-1401: the Empath's nightly ledger — every night's told number,
     *  oldest first ({ day, number }), from the store's own history getter
     *  (her own delivered rows; per-game by construction). Empty for every
     *  other role and part, and while no telling stands. */
    ledger() {
      // FT-1403 phase 1: the registry's flag, not the one role's id
      if (!signFlag(this.roleId, "wantsLedger") || this.part !== "self")
        return [];
      if (!this.myTelling) return [];
      return this.$store.getters["night/myToldLedger"];
    },
  },
  methods: {
    /**
     * FT-1401: where the i-th ledger mark stands — an SVG transform.
     * FT-1401 v2 (user, from the live shots): CLOCKWISE from ONE O'CLOCK
     * (-60°), 30° per night — the clock's own hour posts: night 1 at one
     * o'clock (the shoulder heart's old ground; the ledger replaces it,
     * see the template), night 2 at two, night 3 at three. Mark centres
     * at r=58 from the coin's centre (50,50) — outside the rim (FT-1386).
     * THE CROWDING RULE: 30° holds 12 nights (a 13th full step would land
     * back on night 1); past 12 the step compresses evenly to 330°/(n-1)
     * and the marks scale down with it, so the whole history stays on the
     * ring — arc-extend then shrink, never a cap. Games end well inside 12.
     */
    ledgerAt(i) {
      const n = this.ledger.length;
      const step = n <= 12 ? 30 : 330 / (n - 1);
      const scale = Math.min(1, step / 30);
      const a = ((i * step - 60) * Math.PI) / 180;
      const x = 50 + 58 * Math.cos(a);
      const y = 50 + 58 * Math.sin(a);
      return (
        "translate(" +
        x.toFixed(1) +
        " " +
        y.toFixed(1) +
        ")" +
        (scale < 1 ? " scale(" + scale.toFixed(3) + ")" : "")
      );
    },
    /** A four-point star path at (cx, cy), radius r — the Fortune Teller's
     *  sparkles. Geometry in the template stays declarative; only the shape
     *  maths lives here. */
    star(cx, cy, r) {
      const w = r * 0.28;
      return (
        `M ${cx} ${cy - r} L ${cx + w} ${cy - w} L ${cx + r} ${cy} ` +
        `L ${cx + w} ${cy + w} L ${cx} ${cy + r} L ${cx - w} ${cy + w} ` +
        `L ${cx - r} ${cy} L ${cx - w} ${cy - w} Z`
      );
    },
    /** A slender feather at (cx, cy) — a leaf body with a spine, drifting
     *  point-down. One path so a group transform moves it whole. */
    feather(cx, cy) {
      return (
        `M ${cx} ${cy} q 3.4 -6 1.6 -14 q -5.4 6.4 -1.6 14 Z ` +
        `M ${cx} ${cy} l 1 -13`
      );
    },
  },
};
</script>

<style scoped lang="scss">
// FT-1403 phase 1: each role's PRIMARY ink comes off the shared registry
// map now (golem/_roleInk.scss — the same entries Player.vue's glow maps
// are built from), so the two files can no longer drift per role. The
// hot/deep/secondary shades below stay local: they are this file's own
// working colours, not the cross-file identity ink.
@import "../golem/roleInk.scss";

// The mark fills the night-pick box (the coin's square) and spills 24 units
// of headroom above it for shoulder-borne art. Never a control.
.nm {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;

  svg {
    position: absolute;
    left: 0;
    // 124 viewBox units on a 100-unit-wide box; the extra 24 sit above.
    top: -24%;
    width: 100%;
    height: 124%;
    overflow: visible;
  }
}

// ── THE MONK'S PALETTE ──────────────────────────────────────────────────
// White-gold — hotter and paler than the coins' brass so it reads as LIGHT
// laid on metal, with a dark under-shadow doing the edge work the way every
// bright mark on this dial does (the vote marks' halo rule).
$mk-gold: map-get($role-ink, monk);
$mk-gold-hot: #fff6dc;

// state 1 — the rosary beads. Dotted ring just outside the rim (FT-1386),
// breathing by opacity. THE ONE LOOP THIS FILE IS ALLOWED (the invite
// breathe).
.mk-beads {
  fill: none;
  stroke: $mk-gold-hot;
  stroke-width: 3.4;
  stroke-linecap: round;
  // 0.1-long dashes with round caps read as beads, not dashes
  stroke-dasharray: 0.1 6.9;
  filter: drop-shadow(0 0 2px rgba(0, 0, 0, 0.95))
    drop-shadow(0 0 5px rgba(255, 233, 176, 0.8));
  animation: mk-breathe 2s ease-in-out infinite alternate;
}

// a frame caught at the bottom of the cycle still reads — the
// seat-move-invite rule, kept here for every breathe that follows
@keyframes mk-breathe {
  from {
    opacity: 0.5;
  }
  to {
    opacity: 1;
  }
}

// state 2 — the halo hovers over the chosen coin. One settle-in (≤0.6s),
// then it HOLDS; the rays wake once with it.
.mk-halo {
  fill: none;
  stroke: $mk-gold-hot;
  stroke-width: 3;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.85))
    drop-shadow(0 0 6px rgba(255, 233, 176, 0.8));
}

.mk-hover {
  animation: mk-hover-in 0.6s ease-out both;
}

@keyframes mk-hover-in {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.mk-ray {
  stroke: $mk-gold-hot;
  stroke-width: 2;
  stroke-linecap: round;
  opacity: 0;
  animation: mk-ray-wake 0.5s ease-out 0.35s both;
  &.mk-ray2 {
    animation-delay: 0.45s;
  }
  &.mk-ray3 {
    animation-delay: 0.55s;
  }
}

@keyframes mk-ray-wake {
  from {
    opacity: 0;
  }
  to {
    opacity: 0.85;
  }
}

// state 3 — the seal. The halo drops the last stretch onto the rim, the
// bell-flash rings ONCE (an expanding ring that burns out), and what holds
// is a solid ring plus a soft dome of light over the coin's crown.
.mk-landed {
  animation: mk-halo-land 0.45s ease-in both;
}

@keyframes mk-halo-land {
  from {
    opacity: 0;
    transform: translateY(-14px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.mk-toll {
  fill: none;
  stroke: $mk-gold-hot;
  stroke-width: 3;
  transform-origin: 50px 50px;
  animation: mk-toll-once 0.8s ease-out 0.3s both;
}

@keyframes mk-toll-once {
  from {
    opacity: 0.95;
    transform: scale(1);
  }
  to {
    opacity: 0;
    transform: scale(1.22);
  }
}

.mk-dome {
  fill: $mk-gold;
  opacity: 0;
  animation: mk-dome-in 0.5s ease-out 0.45s both;
}

@keyframes mk-dome-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 0.5;
  }
}

.mk-seal-ring {
  fill: none;
  stroke: $mk-gold;
  stroke-width: 2.4;
  filter: drop-shadow(0 0 2px rgba(0, 0, 0, 0.85))
    drop-shadow(0 0 7px rgba(255, 233, 176, 0.7));
  opacity: 0;
  animation: mk-seal-hold 0.45s ease-out 0.4s both;
}

@keyframes mk-seal-hold {
  from {
    opacity: 0;
  }
  to {
    opacity: 0.9;
  }
}

// ── THE POISONER'S PALETTE ──────────────────────────────────────────────
// Sick toxic green — nothing else on the square speaks it (the yes-chip's
// #7ed67e is a leaf green and lives only in answer pills), so venom cannot
// be mistaken for anything benign.
$ps-green: map-get($role-ink, poisoner);
$ps-green-hot: #c8ff6e;
// the seal's ink — DEEPER than the staged glow (the user's pick: the
// confirmation is the darker circle, not a brighter flash)
$ps-green-deep: #4a8d1e;

// state 1 — the seep ring breathes, and one bright bead slowly rounds it.
.ps-seep-ring {
  fill: none;
  stroke: $ps-green;
  stroke-width: 2.4;
  stroke-dasharray: 5 4;
  filter: drop-shadow(0 0 2px rgba(0, 0, 0, 0.95))
    drop-shadow(0 0 5px rgba(143, 227, 60, 0.7));
  animation: mk-breathe 2s ease-in-out infinite alternate;
}

.ps-bead {
  fill: none;
  stroke: $ps-green-hot;
  stroke-width: 3.6;
  stroke-linecap: round;
  stroke-dasharray: 0.045 0.955;
  filter: drop-shadow(0 0 4px rgba(200, 255, 110, 0.9));
  animation: ps-round 6s linear infinite;
}

@keyframes ps-round {
  from {
    stroke-dashoffset: 0;
  }
  to {
    stroke-dashoffset: -1;
  }
}

// state 2 — the miasma (FT-1395, the user-picked v3 aura): a soft toxic
// glow rises BEHIND the chosen coin — a heavily blurred ring hugging the
// rim, weighted low the way the concept shot read. One fade-in, then it
// HOLDS (the no-loop rule for a decision at rest).
.ps-aura {
  fill: none;
  stroke: $ps-green;
  stroke-width: 9;
  stroke-linecap: round;
  filter: blur(4px);
  opacity: 0;
  animation: ps-haze-in 0.7s ease-out both;
}

@keyframes ps-haze-in {
  from {
    opacity: 0;
  }
  to {
    opacity: 0.42;
  }
}

// the lower reach glows hotter — the venom pools where the coin meets the
// cloth, the shot's read without its detached foot-fog band
.ps-aura-low {
  fill: none;
  stroke: $ps-green-hot;
  stroke-width: 10;
  stroke-linecap: round;
  filter: blur(3px);
  opacity: 0;
  animation: ps-haze-low 0.7s ease-out 0.15s both;
}

@keyframes ps-haze-low {
  from {
    opacity: 0;
    transform: translateY(5px);
  }
  to {
    opacity: 0.6;
    transform: translateY(0);
  }
}

// ps-crawl and ps-surface outlived the seep dress they were drawn for —
// the Washerwoman's ring and the Butler's spark still ride them (the Imp's
// slashes did too, until FT-1384 v3 retired the claws).
@keyframes ps-crawl {
  from {
    stroke-dashoffset: 1;
  }
  to {
    stroke-dashoffset: 0;
  }
}

@keyframes ps-surface {
  from {
    opacity: 0;
    transform: translateY(3px);
  }
  to {
    opacity: 0.95;
    transform: translateY(0);
  }
}

// state 3 — the seal: one DEEPER-green circle closes round the coin in a
// single sweep and goes STILL; the staged aura holds on underneath,
// dimmed, so the poisoned air never snaps off at the Confirm.
.ps-seal-ring {
  fill: none;
  stroke: $ps-green-deep;
  stroke-width: 3.8;
  stroke-linecap: round;
  stroke-dasharray: 1;
  filter: drop-shadow(0 0 2px rgba(0, 0, 0, 0.9))
    drop-shadow(0 0 6px rgba(74, 141, 30, 0.8));
  animation: ps-sweep 0.8s ease-in-out both;
}

@keyframes ps-sweep {
  from {
    stroke-dashoffset: 1;
    opacity: 0.7;
  }
  to {
    stroke-dashoffset: 0;
    opacity: 0.95;
  }
}

.ps-aura-dim {
  animation: ps-haze-dim 0.6s ease-out both;
}

@keyframes ps-haze-dim {
  from {
    opacity: 0;
  }
  to {
    opacity: 0.22;
  }
}

// ── THE FORTUNE TELLER'S PALETTE ────────────────────────────────────────
// Starlight — the thread's own pale violet-white (NightThread.vue), so the
// coin marks and the line between them read as one act.
$ft-star: map-get($role-ink, fortuneteller);
$ft-star-hot: #f4eeff;

// state 1 — sparkles wink in and out around the coin. The state-1 loop.
.ft-spark {
  fill: $ft-star-hot;
  // scale around the star's own centre, not the SVG origin — without this
  // a wink at (84,20) would slide toward the corner as it shrank
  transform-box: fill-box;
  transform-origin: center;
  filter: drop-shadow(0 0 2px rgba(0, 0, 0, 0.9))
    drop-shadow(0 0 4px rgba(217, 204, 255, 0.85));
  animation: ft-wink 2s ease-in-out infinite;
  &.ft-s2 {
    animation-delay: 0.5s;
  }
  &.ft-s3 {
    animation-delay: 1s;
  }
  &.ft-s4 {
    animation-delay: 1.5s;
  }
}

@keyframes ft-wink {
  0%,
  100% {
    opacity: 0.15;
    transform: scale(0.8);
  }
  50% {
    opacity: 1;
    transform: scale(1.05);
  }
}

// staged sparkles hold still beside the crescent — no loop at rest.
.ft-spark.ft-held {
  animation: mk-seal-hold 0.4s ease-out 0.3s both;
}

// state 2 — the crescent veils the picked coin's shoulder, one settle-in.
.ft-crescent {
  fill: $ft-star;
  filter: drop-shadow(0 0 2px rgba(0, 0, 0, 0.9))
    drop-shadow(0 0 6px rgba(217, 204, 255, 0.8));
  animation: ft-veil 0.6s ease-out both;
}

@keyframes ft-veil {
  from {
    opacity: 0;
    transform: translateY(-6px);
  }
  to {
    opacity: 0.92;
    transform: translateY(0);
  }
}

// state 3 — the eye OPENS and stays open: lids part (a scaleY unfold), the
// iris arrives with them. Dark pupil, hot rim — the register's rule.
.ft-eye {
  transform-origin: 50px 8px;
  animation: ft-open 0.55s ease-out 0.2s both;

  .ft-lid {
    fill: rgba(20, 14, 40, 0.85);
    stroke: $ft-star-hot;
    stroke-width: 1.6;
    filter: drop-shadow(0 0 2px rgba(0, 0, 0, 0.9))
      drop-shadow(0 0 6px rgba(244, 238, 255, 0.8));
  }
  .ft-iris {
    fill: $ft-star;
  }
  .ft-pupil {
    fill: #14092c;
  }
}

@keyframes ft-open {
  from {
    opacity: 0;
    transform: scaleY(0.1);
  }
  to {
    opacity: 1;
    transform: scaleY(1);
  }
}

// ── THE BUTLER'S PALETTE ────────────────────────────────────────────────
// Champagne ribbon — NightThread's cord colours, so the bow on the coin and
// the cord reaching it read as one piece of ribbon.
$bt-cord: map-get($role-ink, butler);
$bt-cord-hot: #ffe9c4;

.bt-bow {
  .bt-loop {
    fill: rgba(58, 40, 16, 0.75);
    stroke: $bt-cord;
    stroke-width: 1.8;
    stroke-linejoin: round;
    filter: drop-shadow(0 0 2px rgba(0, 0, 0, 0.9))
      drop-shadow(0 0 4px rgba(232, 201, 143, 0.6));
  }
  .bt-tail {
    fill: none;
    stroke: $bt-cord;
    stroke-width: 1.8;
    stroke-linecap: round;
    filter: drop-shadow(0 0 2px rgba(0, 0, 0, 0.9));
  }
  .bt-knot {
    fill: $bt-cord-hot;
  }
}

// state 1 — the bow sways gently at the coin's shoulder. The state-1 loop.
.bt-sway {
  transform-origin: 78px 22px;
  animation: bt-sway 2s ease-in-out infinite alternate;
}

@keyframes bt-sway {
  from {
    transform: rotate(-7deg);
    opacity: 0.65;
  }
  to {
    transform: rotate(7deg);
    opacity: 1;
  }
}

// state 2 — the bow settles on the master, loose, and holds.
.bt-loose {
  transform-origin: 78px 22px;
  animation: bt-settle 0.6s ease-out both;
}

@keyframes bt-settle {
  from {
    opacity: 0;
    transform: translateY(-8px) rotate(-6deg);
  }
  to {
    opacity: 1;
    transform: translateY(0) rotate(0deg);
  }
}

// state 3 — the CINCH: the bow snaps a size smaller and brightens, one
// crack, in step with the cord going taut one component over.
.bt-cinched {
  transform-origin: 78px 22px;
  animation: bt-cinch 0.4s ease-out both;

  .bt-loop {
    stroke: $bt-cord-hot;
    filter: drop-shadow(0 0 2px rgba(0, 0, 0, 0.9))
      drop-shadow(0 0 6px rgba(255, 233, 196, 0.85));
  }
  .bt-tail {
    stroke: $bt-cord-hot;
  }
}

@keyframes bt-cinch {
  0% {
    transform: scale(1.25);
    opacity: 0.5;
  }
  55% {
    transform: scale(0.94);
    opacity: 1;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.bt-spark {
  fill: $bt-cord-hot;
  filter: drop-shadow(0 0 4px rgba(255, 233, 196, 0.9));
  opacity: 0;
  animation: ps-surface 0.35s ease-out 0.4s both;
}

// ── THE IMP'S PALETTE ───────────────────────────────────────────────────
// Ember orange — fire, not blood: crimson already means death and the
// bluffs mask on this square, and the rune is a signature, not a wound.
// (v4: the ember inks survive only in the retired arc/tick/slash rules —
// the live dress is one-ink red end to end, the user's call.)
$im-ember: #ff9a3d;
$im-ember-hot: #ffc76e;
$im-deep: #d24a12;
// FT-1384 v2 — the invite's red: the ember family pushed to actual red
// (the kill invitation reads as blood-adjacent, still fire-warm).
// v3 (user vet): #ff4b33 still READ orange on the dial — true red now
// (hue 0, the #e01818–#ff2020 band), bright enough to hold on the dark
// cloth and beside the brass; the dark under-shadow keeps the edge work.
$im-red: map-get($role-staged-ink, imp);
// v4 (user vet): "all red and a full circle" — the whole dress is one-ink
// red now, so the sealed flare/embers take red's own hot tint, not ember's.
$im-red-hot: #ff6a52;
// v5 (user): "the border on the sealed can be purple" — the SEALED ring
// leaves red to the pentagram and takes the app's own sealed/locked purple
// (the FT-1108 family, #a78fcd — the ink the generic seal already speaks).
// Purple ring over red glyph is the intended contrast; the flare and the
// embers follow the ring (one seal, one ink for its motion). Staged stays
// exactly as v4 shipped: one solid red circle.
$im-seal-purple: map-get($role-ink, imp);
$im-seal-purple-hot: #cdbdf2;

// state 1 (FT-1384 v2, 2026-09-05) — a RED DOTTED border round every legal
// cog: the told-candidate ring's dash rhythm (0.012 0.028 on pathLength=1,
// round caps → dots) in the Imp's red; one ≤1s arrival, then held still —
// no flicker loop. Replaces the ember ticks (retired below, kept per
// never-delete).
.im-invite-ring {
  fill: none;
  stroke: $im-red;
  stroke-width: 2.8;
  stroke-linecap: round;
  stroke-dasharray: 0.012 0.028;
  filter: drop-shadow(0 0 2px rgba(0, 0, 0, 0.9))
    drop-shadow(0 0 4px rgba(240, 30, 30, 0.7));
  opacity: 0;
  animation: nm-arrive 0.5s ease-out both;
  // v3: the hover melt below — an affordance, not an animation
  transition: stroke-dasharray 0.15s ease-out;
}

// FT-1384 v3 (user vet): HOVERING a legal cog melts its dotted invitation
// SOLID — same red, same radius, the dash runs continuous in ≤150ms: the
// mouse asking "this one?" and the ring answering yes. Mouse-only by
// construction (touch never enters :hover, and that is fine). The ancestor
// half of the selector is Player.vue's .night-pick box — the coin's own
// hover surface; the scoped compiler stamps only the ring, so the
// cross-component reach is deliberate and works as written.
.night-pick:hover .im-invite-ring {
  stroke-dasharray: 1 0;
}

// state 2 (FT-1384 v4, user vet) — ONE full solid red circle round the
// picked coin: the invite's hover-melt ring, persisting. It and the
// pentagram are the whole staged dress — the ember open arc retired below,
// the purple pick ring stood down in Player.vue.
.im-staged-ring {
  fill: none;
  stroke: $im-red;
  stroke-width: 3;
  stroke-linecap: round;
  filter: drop-shadow(0 0 2px rgba(0, 0, 0, 0.9))
    drop-shadow(0 0 5px rgba(240, 30, 30, 0.75));
  opacity: 0;
  animation: nm-arrive 0.5s ease-out both;
}

// the retired ember ticks' dress — the template no longer renders .im-tick
// (FT-1384 v2); the rule stays per never-delete.
// state 1 — ember ticks flicker round the rim. The state-1 loop, staggered
// four ways so the ring smoulders rather than blinking in unison.
.im-tick {
  fill: none;
  stroke: $im-ember;
  stroke-width: 2.6;
  stroke-linecap: round;
  filter: drop-shadow(0 0 2px rgba(0, 0, 0, 0.9))
    drop-shadow(0 0 4px rgba(255, 154, 61, 0.75));
  animation: im-flicker 2s ease-in-out infinite;
  &.im-t1 {
    animation-delay: 0.5s;
  }
  &.im-t2 {
    animation-delay: 1s;
  }
  &.im-t3 {
    animation-delay: 1.5s;
  }
}

@keyframes im-flicker {
  0%,
  100% {
    opacity: 0.25;
  }
  50% {
    opacity: 1;
  }
}

// state 2 — the claw: three slashes rake IN one after another, and the rune
// arc traces itself but stays OPEN (the circle is the seal's to close).
.im-slash {
  fill: none;
  stroke: $im-deep;
  stroke-width: 3.4;
  stroke-linecap: round;
  stroke-dasharray: 1;
  filter: drop-shadow(0 0 2px rgba(0, 0, 0, 0.9))
    drop-shadow(0 0 4px rgba(255, 154, 61, 0.65));
  animation: ps-crawl 0.3s ease-in both;
  &.im-s2 {
    animation-delay: 0.18s;
  }
  &.im-s3 {
    animation-delay: 0.36s;
  }
}

// the retired open arc's dress — the template no longer renders
// .im-rune-arc (FT-1384 v4: the staged circle is full and red now); the
// rule stays per never-delete.
.im-rune-arc {
  fill: none;
  stroke: $im-ember;
  stroke-width: 2.6;
  stroke-linecap: round;
  // 72% of the circle: an open arc, unmistakably unfinished
  stroke-dasharray: 0.72 0.28;
  filter: drop-shadow(0 0 2px rgba(0, 0, 0, 0.9))
    drop-shadow(0 0 5px rgba(255, 154, 61, 0.7));
  opacity: 0;
  animation: nm-arrive 0.5s ease-out 0.5s both;
}

@keyframes nm-arrive {
  from {
    opacity: 0;
  }
  to {
    opacity: 0.9;
  }
}

// state 3 — the SNAP: the circle closes in one fast sweep, the pentagram
// flares with it, one bright ring of light burns out, and the embers drift
// up and die. Then everything holds still.
.im-rune-ring {
  fill: none;
  // v4 was red, matching the staged circle it closes over; v5 (user) seals
  // in PURPLE — the locked ink closing over the red the stage left.
  stroke: $im-seal-purple;
  stroke-width: 3;
  stroke-linecap: round;
  stroke-dasharray: 1;
  filter: drop-shadow(0 0 2px rgba(0, 0, 0, 0.9))
    drop-shadow(0 0 7px rgba(167, 143, 205, 0.8));
  animation: ps-sweep 0.45s ease-in both;
}

// FT-1384 v3: the pentagram is the STAGED mark now — it lands with the
// pick (red family, the invitation's own ink) and simply STANDS when the
// seal closes round it. Was $im-ember-hot with a 0.4s delay behind the
// sealed ring-sweep; the delay went with the claws it was staged behind.
.im-penta {
  fill: none;
  stroke: $im-red;
  stroke-width: 2;
  stroke-linejoin: round;
  filter: drop-shadow(0 0 2px rgba(0, 0, 0, 0.9))
    drop-shadow(0 0 5px rgba(240, 30, 30, 0.75));
  opacity: 0;
  animation: nm-arrive 0.4s ease-out both;
}

// sealed re-mounts the glyph (the template branch swaps), so the held
// variant arrives ALREADY standing — no second draw-in, no flare-timed
// fade: the rune closes over the mark the stage left. One mark completing.
.im-penta-held {
  animation: none;
  opacity: 0.9;
}

.im-flare {
  fill: none;
  // v5: the flare is the ring's own flash, so it follows the ring to purple.
  stroke: $im-seal-purple-hot;
  stroke-width: 3;
  transform-origin: 50px 50px;
  animation: mk-toll-once 0.6s ease-out 0.4s both;
}

.im-slash.im-hard {
  stroke: $im-ember;
  animation: mk-seal-hold 0.3s ease-out both;
}

.im-ember {
  // v5: the embers rise off the sealing ring, so they wear its purple too.
  fill: $im-seal-purple-hot;
  filter: drop-shadow(0 0 3px rgba(205, 189, 242, 0.9));
  opacity: 0;
  animation: im-rise 0.8s ease-out 0.5s both;
  &.im-e2 {
    animation-delay: 0.6s;
  }
  &.im-e3 {
    animation-delay: 0.7s;
  }
  &.im-e4 {
    animation-delay: 0.8s;
  }
}

// up, and out — the embers are the one sealed element that ENDS dark,
// because an ember that holds is a lamp, not a death.
@keyframes im-rise {
  0% {
    opacity: 0;
    transform: translateY(4px);
  }
  35% {
    opacity: 1;
  }
  100% {
    opacity: 0;
    transform: translateY(-9px);
  }
}

// ── THE RAVENKEEPER'S PALETTE ───────────────────────────────────────────
// Moonlit violet — a dead seat's mark, so it is the coolest and quietest of
// the six: violet feathers, a pale moon, one glint.
$rk-violet: map-get($role-ink, ravenkeeper);
$rk-violet-hot: #e6dcff;

.rk-feather {
  fill: rgba(46, 32, 84, 0.8);
  stroke: $rk-violet;
  stroke-width: 1.3;
  stroke-linecap: round;
  filter: drop-shadow(0 0 2px rgba(0, 0, 0, 0.9))
    drop-shadow(0 0 4px rgba(164, 140, 224, 0.6));
}

// FT-1384 v5 (user): the invite is the FEATHER RING now — the Imp
// dotted-border idiom with feathers for the dots: ten small BLUE feathers
// standing evenly on r=55 (outside the rim, FT-1386), told-blue family
// (the townsfolk #1f65ff derivation the Investigator's evidence wears —
// a moonlit blue that cannot be read as the Ravenkeeper's own violet
// dress one state over). One ≤1s arrival, then STILL: no drift, no loop.
$rk-blue: #6695ff;
$rk-blue-hot: #a8c6ff;

.rk-invf {
  opacity: 0;
  animation: nm-arrive 0.5s ease-out both;
}

.rk-invf-feather {
  fill: rgba(15, 28, 58, 0.8);
  stroke: $rk-blue;
  stroke-width: 1.3;
  stroke-linecap: round;
  filter: drop-shadow(0 0 2px rgba(0, 0, 0, 0.9))
    drop-shadow(0 0 4px rgba(102, 149, 255, 0.6));
  // the Imp's hover answer, feather-dressed (below) — stroke and filter
  // only: nm-arrive owns the group's opacity, and an animated property
  // ignores a transition (the Imp dodged the same trap with its dash melt).
  transition:
    stroke 0.15s ease-out,
    filter 0.15s ease-out;
}

// FT-1384 v5: hovering a legal cog BRIGHTENS its feather ring — the same
// "this one?" affordance the Imp's dotted ring answers with its melt.
// Mouse-only by construction (touch never enters :hover, and that is
// fine); the ancestor half is Player.vue's .night-pick box, the deliberate
// cross-component reach the Imp's melt already proved out.
.night-pick:hover .rk-invf-feather {
  stroke: $rk-blue-hot;
  filter: drop-shadow(0 0 2px rgba(0, 0, 0, 0.9))
    drop-shadow(0 0 6px rgba(168, 198, 255, 0.95));
}

// the retired drifting feathers' dress — the template no longer renders
// .rk-drift (FT-1384 v5: the feather ring above is the invite); the rule
// stays per never-delete.
// state 1 — lone feathers drift and tumble slowly past the coin. The
// state-1 loop, staggered so the fall never reads as one object.
.rk-drift {
  animation: rk-drift 3s ease-in-out infinite;
  &.rk-f2 {
    animation-delay: 1s;
  }
  &.rk-f3 {
    animation-delay: 2s;
  }
}

@keyframes rk-drift {
  0% {
    opacity: 0;
    transform: translateY(-8px) rotate(-8deg);
  }
  30% {
    opacity: 0.95;
  }
  70% {
    opacity: 0.85;
  }
  100% {
    opacity: 0;
    transform: translateY(10px) rotate(10deg);
  }
}

// state 2 — the feathers flutter DOWN and settle one by one on the chosen
// rim (staggered ease-out), then REST — the no-loop rule.
.rk-settle {
  animation: rk-settle 0.5s ease-out both;
  &.rk-f2 {
    animation-delay: 0.15s;
  }
  &.rk-f3 {
    animation-delay: 0.3s;
  }
  &.rk-f4 {
    animation-delay: 0.45s;
  }
}

@keyframes rk-settle {
  from {
    opacity: 0;
    transform: translateY(-12px) rotate(-14deg);
  }
  to {
    opacity: 1;
    transform: translateY(0) rotate(0deg);
  }
}

// state 3 — the wings FOLD shut around the coin: both pleated sides sweep
// up from the foot and lock, and the moon-eye glints once above.
.rk-pleat {
  fill: none;
  stroke: $rk-violet;
  stroke-width: 2.4;
  stroke-linecap: round;
  filter: drop-shadow(0 0 2px rgba(0, 0, 0, 0.9))
    drop-shadow(0 0 5px rgba(164, 140, 224, 0.7));
}

.rk-wing {
  transform-origin: 50px 92px;
  animation: rk-fold 0.6s ease-out both;
}

.rk-wl {
  --rk-from: 32deg;
}
.rk-wr {
  --rk-from: -32deg;
}

@keyframes rk-fold {
  from {
    opacity: 0;
    transform: rotate(var(--rk-from, 0deg));
  }
  to {
    opacity: 0.95;
    transform: rotate(0deg);
  }
}

.rk-moon {
  fill: $rk-violet-hot;
  filter: drop-shadow(0 0 2px rgba(0, 0, 0, 0.9))
    drop-shadow(0 0 6px rgba(230, 220, 255, 0.8));
  opacity: 0;
  animation: nm-arrive 0.4s ease-out 0.5s both;
}

// the glint — ONCE, then it dies back to a faint watchful point.
.rk-glint {
  fill: white;
  transform-box: fill-box;
  transform-origin: center;
  filter: drop-shadow(0 0 5px rgba(255, 255, 255, 0.95));
  opacity: 0;
  animation: rk-glint 0.55s ease-out 0.7s both;
}

@keyframes rk-glint {
  0% {
    opacity: 0;
    transform: scale(0.4);
  }
  40% {
    opacity: 1;
    transform: scale(1.15);
  }
  100% {
    opacity: 0.35;
    transform: scale(0.85);
  }
}

// ═══ THE TOLD-INFORMATION PALETTES (FT-1385) ════════════════════════════
// Two beats each: the telling arrives once (≤1s) and holds bright; the
// settled residue is dotted, dimmer, and PERSISTS — the transition between
// the two rides plain CSS transitions on the same elements, so the ease
// from bright to quiet is a settle, not a swap.

// ── THE WASHERWOMAN'S PALETTE ───────────────────────────────────────────
// Laundry white — soap-pale, cooler than the Monk's white-gold: clean
// linen on brass, nothing else on the square speaks it.
$ww-white: map-get($role-ink, washerwoman);
$ww-wood: #3a2c1a;
$ww-wood-lit: #c9a86e;

// the candidate ring: strings itself on at the telling (the draw-on), goes
// dotted-slack at rest. The dash pattern flip is covered by the opacity
// ease, so the settle reads as the line relaxing.
.ww-ring {
  fill: none;
  stroke: $ww-white;
  stroke-width: 2.8;
  stroke-linecap: round;
  stroke-dasharray: 1;
  filter: drop-shadow(0 0 2px rgba(0, 0, 0, 0.9))
    drop-shadow(0 0 6px rgba(234, 242, 255, 0.8));
  animation: ps-crawl 0.6s ease-out both;
  transition:
    opacity 0.7s ease,
    stroke-width 0.7s ease,
    filter 0.7s ease;
}

.nm-settled .ww-ring {
  stroke-width: 2;
  stroke-dasharray: 0.012 0.028;
  opacity: 0.72;
  filter: drop-shadow(0 0 2px rgba(0, 0, 0, 0.9))
    drop-shadow(0 0 3px rgba(234, 242, 255, 0.45));
  animation: none;
}

// the peg: a wooden clothespin clamped on the coin's crown — dark body,
// pale rim light (the register's rule). The telling drops it in with one
// click of overshoot; at rest it simply holds, a touch quieter.
.ww-peg {
  path {
    fill: $ww-wood;
    stroke: $ww-wood-lit;
    stroke-width: 1.4;
    stroke-linejoin: round;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.9))
      drop-shadow(0 0 4px rgba(234, 242, 255, 0.5));
  }
  .ww-spring {
    fill: $ww-white;
  }
  animation: ww-clamp 0.5s ease-in both;
  transition: opacity 0.7s ease;
}

.nm-settled .ww-peg {
  opacity: 0.85;
  animation: none;
}

@keyframes ww-clamp {
  0% {
    opacity: 0;
    transform: translateY(-12px);
  }
  70% {
    opacity: 1;
    transform: translateY(1.5px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

// the teller's own peg — the residue's home mark, resting at the coin's
// shoulder, smaller and off-square like a peg left on the line.
.ww-peg-self {
  transform-origin: 50px 0px;
  animation: ww-clamp 0.5s ease-in both;
}

.nm-part-self.nm-washerwoman svg {
  // the whole self mark rides at the shoulder rather than the crown
  transform: translate(24%, 4%) rotate(24deg) scale(0.72);
}

// soap: three bubbles rise once and pop — telling only, gone at rest
// (the template drops them with the state, so nothing loops).
.ww-bubble {
  fill: none;
  stroke: $ww-white;
  stroke-width: 1.1;
  filter: drop-shadow(0 0 3px rgba(234, 242, 255, 0.8));
  opacity: 0;
  animation: ww-soap 0.9s ease-out 0.35s both;
  &.ww-b2 {
    animation-delay: 0.5s;
  }
  &.ww-b3 {
    animation-delay: 0.65s;
  }
}

@keyframes ww-soap {
  0% {
    opacity: 0;
    transform: translateY(5px);
  }
  35% {
    opacity: 0.95;
  }
  100% {
    opacity: 0;
    transform: translateY(-10px);
  }
}

// ── THE LIBRARIAN'S PALETTE ─────────────────────────────────────────────
// Old parchment sepia for the book's furniture, one blue ribbon — the
// townsfolk #1f65ff family (FT-1392, user rule: no town dress wears red;
// the ribbon was the last red cloth on a good coin).
$lb-sepia: map-get($role-ink, librarian);
$lb-page: #f3ead0;
$lb-blue: #2f5ac8;

.lb-ring {
  fill: none;
  stroke: $lb-sepia;
  stroke-width: 2.8;
  stroke-linecap: round;
  stroke-dasharray: 1;
  filter: drop-shadow(0 0 2px rgba(0, 0, 0, 0.9))
    drop-shadow(0 0 6px rgba(232, 217, 168, 0.75));
  animation: ps-crawl 0.6s ease-out both;
  transition:
    opacity 0.7s ease,
    stroke-width 0.7s ease,
    filter 0.7s ease;
}

.nm-settled .lb-ring {
  stroke-width: 2;
  stroke-dasharray: 0.012 0.028;
  opacity: 0.7;
  filter: drop-shadow(0 0 2px rgba(0, 0, 0, 0.9))
    drop-shadow(0 0 3px rgba(232, 217, 168, 0.4));
  animation: none;
}

// the page-corner: RETIRED by the FT-1400 vet (the ribbon tab below is the
// candidate mark now) — kept per never-delete; restore the .lb-corner group
// in the template to bring the dog-ear back.
.lb-corner {
  .lb-fold {
    fill: rgba(58, 46, 20, 0.82);
    stroke: $lb-page;
    stroke-width: 1.4;
    stroke-linejoin: round;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.9))
      drop-shadow(0 0 4px rgba(243, 234, 208, 0.55));
  }
  .lb-crease {
    fill: none;
    stroke: $lb-page;
    stroke-width: 1;
    opacity: 0.8;
  }
  transform-origin: 93px 0px;
  animation: lb-tuck 0.55s ease-out both;
  transition: opacity 0.7s ease;
}

.nm-settled .lb-corner {
  opacity: 0.85;
  animation: none;
}

@keyframes lb-tuck {
  0% {
    opacity: 0;
    transform: rotate(-24deg);
  }
  75% {
    opacity: 1;
    transform: rotate(4deg);
  }
  100% {
    opacity: 1;
    transform: rotate(0deg);
  }
}

// FT-1400 vet: the ribbon TAB on a candidate coin — the teller's own
// notched bookmark, draped over the crown the way the peg clamps and the
// tack pins (the crown is the candidate marks' shared seat). Told-blue
// cloth, sepia edge — the lb-ribbon inks exactly, so tab and bookmark
// read as cuts of the same ribbon. Drops in with lb-hang at the telling;
// rests a touch quieter, still draped.
.lb-tab {
  path {
    fill: $lb-blue;
    stroke: $lb-sepia;
    stroke-width: 1.2;
    stroke-linejoin: round;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.9))
      drop-shadow(0 0 4px rgba(47, 90, 200, 0.5));
  }
  animation: lb-hang 0.55s ease-out both;
  transition: opacity 0.7s ease;
}

.nm-settled .lb-tab {
  opacity: 0.85;
  animation: none;
}

// the teller's own ribbon — the bookmark hanging from the rim, the place
// kept. Slides down into place once, then hangs.
.lb-ribbon {
  path {
    fill: $lb-blue;
    stroke: $lb-sepia;
    stroke-width: 1.2;
    stroke-linejoin: round;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.9))
      drop-shadow(0 0 4px rgba(47, 90, 200, 0.5));
  }
  animation: lb-hang 0.55s ease-out both;
  transition: opacity 0.7s ease;
}

.nm-settled .lb-ribbon {
  opacity: 0.85;
  animation: none;
}

@keyframes lb-hang {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

// FT-1400: the told character's token above the bookmark — dark disc,
// the book's own sepia rim, the character's icon. Hangs in with the
// ribbon's own drop; the residue holds it a touch quieter, no replay.
.lb-token {
  .lb-token-disc {
    fill: rgba(24, 18, 8, 0.85);
    stroke: $lb-sepia;
    stroke-width: 1.4;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.9))
      drop-shadow(0 0 4px rgba(232, 217, 168, 0.5));
  }
  image {
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.9));
  }
  animation: lb-hang 0.55s ease-out 0.1s both;
  transition: opacity 0.7s ease;
}

.nm-settled .lb-token {
  opacity: 0.85;
  animation: none;
}

.nm-part-self.nm-librarian svg {
  // the ribbon rides at the shoulder, a bookmark's own slight lean
  transform: translate(22%, 2%) rotate(14deg) scale(0.8);
}

// ── THE INVESTIGATOR'S PALETTE ──────────────────────────────────────────
// Evidence blue — the townsfolk team's own #1f65ff (src/vars.scss; the hot
// ink is lighten($townsfolk, 14%), the app's standing derivation). No town
// dress wears red (FT-1392, user rule) — the value structure is the one
// the old red wore: hot when the tack lands, dried darker at rest.
$iv-blue: map-get($role-ink, investigator);
$iv-dry: #3b63c2;
$iv-dark: #0f1c3a;

.iv-ring {
  fill: none;
  stroke: $iv-blue;
  stroke-width: 2.8;
  stroke-linecap: round;
  stroke-dasharray: 1;
  filter: drop-shadow(0 0 2px rgba(0, 0, 0, 0.9))
    drop-shadow(0 0 6px rgba(102, 149, 255, 0.75));
  // faster than the softer roles' read-on: evidence snaps
  animation: ps-crawl 0.4s ease-out both;
  transition:
    opacity 0.7s ease,
    stroke 0.7s ease,
    stroke-width 0.7s ease,
    filter 0.7s ease;
}

.nm-settled .iv-ring {
  stroke: $iv-dry;
  stroke-width: 2;
  stroke-dasharray: 0.012 0.028;
  opacity: 0.75;
  filter: drop-shadow(0 0 2px rgba(0, 0, 0, 0.9))
    drop-shadow(0 0 3px rgba(59, 99, 194, 0.45));
  animation: none;
}

// the tack: a pinhead over the coin's crown with its pin pressed into the
// rim. One HARD press at the telling (scale overshoot in, no bounce out);
// at rest it holds — tacks do not soften.
.iv-tack {
  .iv-pin {
    fill: none;
    stroke: $iv-dry;
    stroke-width: 2;
    stroke-linecap: round;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.9));
  }
  .iv-head {
    fill: $iv-dark;
    stroke: $iv-blue;
    stroke-width: 1.6;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.9))
      drop-shadow(0 0 4px rgba(102, 149, 255, 0.65));
  }
  .iv-glint {
    fill: #d9e6ff;
    opacity: 0.9;
  }
  transform-origin: 50px 0px;
  animation: iv-press 0.4s ease-in both;
  transition: opacity 0.7s ease;
}

.nm-settled .iv-tack {
  opacity: 0.88;
  animation: none;
}

@keyframes iv-press {
  0% {
    opacity: 0;
    transform: translateY(-10px) scale(1.4);
  }
  75% {
    opacity: 1;
    transform: translateY(0.5px) scale(0.95);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

// the teller's own wax seal — pressed once at the telling, cooled at rest.
// Dark wax body, hot red rim light, an embossed boss at its middle.
.iv-seal {
  path {
    fill: $iv-dark;
    stroke: $iv-blue;
    stroke-width: 1.4;
    stroke-linejoin: round;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.9))
      drop-shadow(0 0 4px rgba(102, 149, 255, 0.55));
  }
  .iv-boss {
    fill: none;
    stroke: $iv-blue;
    stroke-width: 1.2;
    opacity: 0.85;
  }
  transform-origin: 50px -1px;
  animation: iv-press 0.45s ease-in both;
  transition: opacity 0.7s ease;
}

.nm-settled .iv-seal {
  opacity: 0.82;
  animation: none;
}

.nm-part-self.nm-investigator svg {
  // the seal rides the shoulder — a stamp on the case, not a crown
  transform: translate(23%, 3%) rotate(-12deg) scale(0.85);
}

// ── THE CHEF'S PALETTE ──────────────────────────────────────────────────
// Kitchen-fire orange — warmer and fatter than the Imp's ember (a hearth,
// not a signature), and the only told dress with no thread: the badge and
// the branded numeral are the whole act.
$ch-ember: map-get($role-ink, chef);
$ch-ember-hot: #ffd98a;
$ch-char: #33200a;

// the badge whole: seared on once, then held; cooled at rest.
.ch-badge {
  animation: ch-sear-in 0.45s ease-out both;
  transition: opacity 0.7s ease;
}

.nm-settled .ch-badge {
  opacity: 0.85;
  animation: none;
}

@keyframes ch-sear-in {
  from {
    opacity: 0;
    transform: scale(1.35);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

// the scorch ring — a ring of heat tracing itself at the telling, a
// dotted char mark at rest.
.ch-scorch {
  fill: none;
  stroke: $ch-ember;
  stroke-width: 2.2;
  stroke-linecap: round;
  stroke-dasharray: 1;
  filter: drop-shadow(0 0 2px rgba(0, 0, 0, 0.9))
    drop-shadow(0 0 6px rgba(255, 179, 71, 0.8));
  animation: ps-crawl 0.5s ease-out both;
  transition:
    opacity 0.7s ease,
    stroke-width 0.7s ease,
    filter 0.7s ease;
}

.nm-settled .ch-scorch {
  stroke-width: 1.6;
  stroke-dasharray: 0.02 0.045;
  opacity: 0.8;
  filter: drop-shadow(0 0 2px rgba(0, 0, 0, 0.9))
    drop-shadow(0 0 3px rgba(255, 179, 71, 0.45));
  animation: none;
}

// the pair-mark — two touching coins, the thing the count counts: char
// bodies, hot rims, deliberately TOUCHING (side-by-side is the rule).
.ch-pair circle {
  fill: $ch-char;
  stroke: $ch-ember-hot;
  stroke-width: 1.4;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.9))
    drop-shadow(0 0 3px rgba(255, 217, 138, 0.6));
}

.nm-settled .ch-pair circle {
  stroke: $ch-ember;
}

// FT-1400 vet: the count at the scorch's centre — hot ember ink at the
// telling, cooled with the badge at rest (ch-badge's own opacity carries
// the settle; the ink steps down with the pair-mark's rule).
.ch-count-num {
  fill: $ch-ember-hot;
  font-family: PiratesBay, sans-serif;
  // 18 in the scorch's 25-unit interior — the Empath's 13-in-18 ratio,
  // so the count stays legible at coin size (the vet's own bar).
  font-size: 18px;
  text-anchor: middle;
  dominant-baseline: central;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.9));
}

.nm-settled .ch-count-num {
  fill: $ch-ember;
}

// ember flecks — lift once at the telling and die dark (the Imp's rule:
// an ember that holds is a lamp). The template drops them at rest.
.ch-fleck {
  fill: $ch-ember-hot;
  filter: drop-shadow(0 0 3px rgba(255, 217, 138, 0.9));
  opacity: 0;
  animation: im-rise 0.8s ease-out 0.35s both;
  &.ch-f2 {
    animation-delay: 0.45s;
  }
  &.ch-f3 {
    animation-delay: 0.55s;
  }
}

.nm-part-self.nm-chef svg {
  // the badge rests at the shoulder, a brand on the coin's crown-side
  transform: translate(24%, 6%) scale(0.9);
}

// ── THE EMPATH'S PALETTE ────────────────────────────────────────────────
// Heart pink — the veins' own ink (NightThread), the warmest colour on
// the ring and nothing else's.
$em-pink: map-get($role-ink, empath);
$em-pink-hot: #ffd2e8;
$em-dark: #401428;

.em-ring {
  fill: none;
  stroke: $em-pink;
  stroke-width: 2.8;
  stroke-linecap: round;
  stroke-dasharray: 1;
  filter: drop-shadow(0 0 2px rgba(0, 0, 0, 0.9))
    drop-shadow(0 0 6px rgba(255, 159, 208, 0.75));
  animation: ps-crawl 0.55s ease-out both;
  transition:
    opacity 0.7s ease,
    stroke-width 0.7s ease,
    filter 0.7s ease;
}

.nm-settled .em-ring {
  stroke-width: 2;
  stroke-dasharray: 0.012 0.028;
  opacity: 0.68;
  filter: drop-shadow(0 0 2px rgba(0, 0, 0, 0.9))
    drop-shadow(0 0 3px rgba(255, 159, 208, 0.4));
  animation: none;
}

// the heart bead: lands on the neighbour's rim with ONE heartbeat — two
// quick swells inside the arrival second, then rest. Dark body, hot pink
// rim light, the register's rule.
.em-heart {
  fill: $em-dark;
  stroke: $em-pink;
  stroke-width: 1.5;
  stroke-linejoin: round;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.9))
    drop-shadow(0 0 4px rgba(255, 159, 208, 0.65));
  transform-origin: 50px -4px;
  animation: em-beat 0.9s ease-out both;
  transition: opacity 0.7s ease;
}

.nm-settled .em-heart {
  opacity: 0.8;
  animation: none;
}

@keyframes em-beat {
  0% {
    opacity: 0;
    transform: scale(0.4);
  }
  25% {
    opacity: 1;
    transform: scale(1.22);
  }
  45% {
    transform: scale(0.96);
  }
  65% {
    transform: scale(1.12);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

// her own heart — the knowledge kept at her shoulder, beating in once with
// the same pulse and resting smaller.
.em-heart-self {
  stroke: $em-pink-hot;
}

// FT-1400: the told count beside her heart — the small-badge idiom (the
// Chef's own move: a ring holding the figure, dotted once it cools). The
// centre's numeral chip dies with the ask; this one stays all game.
// RETIRED by FT-1401 (the nightly ledger keeps the figures now) — CSS kept
// per never-delete; restore the .em-count group in the template to bring
// the single badge back.
.em-count {
  .em-count-ring {
    fill: rgba(64, 20, 40, 0.82);
    stroke: $em-pink;
    stroke-width: 1.6;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.9))
      drop-shadow(0 0 4px rgba(255, 159, 208, 0.55));
  }
  .em-count-num {
    fill: $em-pink-hot;
    font-family: PiratesBay, sans-serif;
    font-size: 13px;
    text-anchor: middle;
    dominant-baseline: central;
  }
  animation: nm-arrive 0.5s ease-out 0.4s both;
  transition: opacity 0.7s ease;
}

.nm-settled .em-count {
  opacity: 0.85;
  animation: none;

  .em-count-ring {
    stroke: rgba(255, 159, 208, 0.6);
    stroke-dasharray: 2 2.4;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.9))
      drop-shadow(0 0 3px rgba(255, 159, 208, 0.35));
  }
}

.nm-part-self.nm-empath svg {
  transform: translate(23%, 4%) rotate(10deg) scale(0.8);
}

// ── FT-1401: THE NIGHTLY LEDGER ─────────────────────────────────────────
// The readings accumulate around her own coin, night by night in wake
// order — a heart for 0, a blood-red ringed numeral for 1 or 2. RED IS
// DELIBERATE and the user's explicit pick for this element: the FT-1392
// no-red-on-town rule is user-exempted here (an evil count wears the
// alarm's colour). FT-1401 v2 (user): the red goes to DRIED BLOOD —
// deeper than the app's alarm #f01e1e — and every numeral is circled,
// the framing the heart mark's stroked body already gives the zeroes.
$em-led-red: #c01515;

.nm-part-self.nm-empath svg.em-ledger {
  // the ledger circles the COIN — it stands off the shoulder transform the
  // rest of the self dress rides.
  transform: none;
}

// each mark arrives once (the newest night's landing) and then holds — a
// history at rest never loops.
.em-led {
  animation: nm-arrive 0.5s ease-out 0.3s both;
  transition: opacity 0.7s ease;
}

.nm-settled .em-led {
  opacity: 0.85;
  animation: none;
}

// the zero-night heart: her own dark-body/pink-rim heart — "no evil
// neighbours" is kept knowledge, in her own ink. FT-1401 v3 (user vet):
// GROWN ×1.75 with its path (stroke-width 1.2 → 2.1, the same glyph
// scaled), ring-mark-sized so the clean nights read at a glance.
// FT-1401 v4 (user): r2 heart back — the path is round 2's own "d"
// (see template), scaled ×1.75 by the wrapping <g>; stroke-width goes
// back to round 2's 1.2 so the group's scale produces the same 2.1
// effective weight round 3 shipped (the coin keeps its size).
.em-led-heart {
  fill: $em-dark;
  stroke: $em-pink;
  stroke-width: 1.2;
  stroke-linejoin: round;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.9))
    drop-shadow(0 0 3px rgba(255, 159, 208, 0.55));
}

// FT-1401 v2: the numeral's ring — dark ground, dried-blood rim, the
// heart mark's own dark-body/bright-rim framing spoken in the alarm ink.
.em-led-ring {
  fill: rgba(26, 5, 5, 0.85);
  stroke: $em-led-red;
  stroke-width: 1.4;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.9))
    drop-shadow(0 0 3px rgba(192, 21, 21, 0.55));
}

// the counted night: the figure itself, dried blood on the ring's dark
// ground — a warm glow keeps the deep red legible on the cloth.
// 13px — the em-count badge's own legibility bar (the FT-1400 ratio).
.em-led-num {
  fill: $em-led-red;
  font-family: PiratesBay, sans-serif;
  font-size: 13px;
  text-anchor: middle;
  dominant-baseline: central;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.95))
    drop-shadow(0 0 3px rgba(240, 96, 96, 0.5));
}

// FT-1401 v4 (user): drips retired — red alone. The blood run's ink +
// rule kept-commented per never-delete:
// $em-led-deep: #7d0e0e;
//
// .em-led-blood {
//   .em-led-drip,
//   .em-led-drop {
//     fill: $em-led-red;
//   }
//   .em-led-dtip {
//     fill: $em-led-deep;
//   }
//   filter: drop-shadow(0 1px 1px rgba(0, 0, 0, 0.9));
// }

// ── THE GRANDMOTHER'S PALETTE (FT-1402) ─────────────────────────────────
// Wire-rim gold — warmer and deeper than the Librarian's washed parchment,
// spectacle brass by lamplight; the lenses are dark glass with the gold
// rim, the register's dark-body/hot-rim rule.
$gm-gold: map-get($role-ink, grandmother);
$gm-glass: rgba(20, 14, 6, 0.78);

// the grandchild's ring: strings itself on at the telling, rests dotted —
// but never dims far: this residue is the one whose persistence is the
// point (her life hangs on the seat it circles).
.gm-ring {
  fill: none;
  stroke: $gm-gold;
  stroke-width: 2.8;
  stroke-linecap: round;
  stroke-dasharray: 1;
  filter: drop-shadow(0 0 2px rgba(0, 0, 0, 0.9))
    drop-shadow(0 0 6px rgba(224, 180, 92, 0.75));
  animation: ps-crawl 0.6s ease-out both;
  transition:
    opacity 0.7s ease,
    stroke-width 0.7s ease,
    filter 0.7s ease;
}

.nm-settled .gm-ring {
  stroke-width: 2;
  stroke-dasharray: 0.012 0.028;
  opacity: 0.72;
  filter: drop-shadow(0 0 2px rgba(0, 0, 0, 0.9))
    drop-shadow(0 0 3px rgba(224, 180, 92, 0.45));
  animation: none;
}

// the spectacles: settle onto the crown once (the peg/tack/tab seat) and
// then HOLD — at rest they stay near-full, the most legible settled mark
// on the square, deliberately (see the template note).
.gm-specs {
  .gm-lens {
    fill: $gm-glass;
    stroke: $gm-gold;
    stroke-width: 1.6;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.9))
      drop-shadow(0 0 4px rgba(224, 180, 92, 0.55));
  }
  .gm-wire {
    fill: none;
    stroke: $gm-gold;
    stroke-width: 1.4;
    stroke-linecap: round;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.9));
  }
  animation: gm-settle 0.55s ease-out both;
  transition: opacity 0.7s ease;
}

.nm-settled .gm-specs {
  opacity: 0.92;
  animation: none;
}

@keyframes gm-settle {
  0% {
    opacity: 0;
    transform: translateY(-11px);
  }
  75% {
    opacity: 1;
    transform: translateY(1.5px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

// her own coin: the grandchild's character token above the shoulder (the
// Librarian's token idiom in her gold) and her folded spectacles beneath —
// WHAT the grandchild is, kept where the residue lives.
.gm-token {
  .gm-token-disc {
    fill: rgba(24, 18, 8, 0.85);
    stroke: $gm-gold;
    stroke-width: 1.4;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.9))
      drop-shadow(0 0 4px rgba(224, 180, 92, 0.5));
  }
  image {
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.9));
  }
  animation: gm-settle 0.55s ease-out 0.1s both;
  transition: opacity 0.7s ease;
}

.nm-settled .gm-token {
  opacity: 0.88;
  animation: none;
}

.gm-specs-self {
  transform-origin: 50px -5px;
  animation: gm-settle 0.55s ease-out both;
}

.nm-part-self.nm-grandmother svg {
  // the keepsakes rest at the shoulder, a reading-chair lean
  transform: translate(23%, 3%) rotate(12deg) scale(0.78);
}

// ── THE UNDERTAKER'S PALETTE (FT-1412) ──────────────────────────────────
// Headstone silver — a cold graveyard grey, the coolest ink on the square
// (every other told ink is warm or team-blue-adjacent); the token's disc is
// wet earth under it, the register's dark-body/hot-rim rule.
$ut-stone: map-get($role-ink, undertaker);
$ut-earth: rgba(14, 14, 16, 0.86);

// the executed character's token: LOWERED onto the shoulder at the telling
// — one drop into the ground with a small overshoot (the coffin's settle),
// then still; settled it holds quieter until the next telling replaces it.
.ut-token {
  .ut-token-disc {
    fill: $ut-earth;
    stroke: $ut-stone;
    stroke-width: 1.4;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.9))
      drop-shadow(0 0 4px rgba(184, 196, 207, 0.5));
  }
  image {
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.9));
  }
  animation: ut-lower 0.6s ease-out both;
  transition: opacity 0.7s ease;
}

.nm-settled .ut-token {
  opacity: 0.85;
  animation: none;
}

// the spade beneath: plants itself a beat after the token lands
.ut-spade {
  .ut-grip {
    fill: none;
    stroke: $ut-stone;
    stroke-width: 1.3;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.9));
  }
  .ut-shaft {
    fill: none;
    stroke: $ut-stone;
    stroke-width: 1.6;
    stroke-linecap: round;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.9));
  }
  .ut-blade {
    fill: $ut-earth;
    stroke: $ut-stone;
    stroke-width: 1.4;
    stroke-linejoin: round;
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.9))
      drop-shadow(0 0 3px rgba(184, 196, 207, 0.45));
  }
  transform-origin: 50px 4px;
  animation: ut-plant 0.5s ease-out 0.15s both;
  transition: opacity 0.7s ease;
}

.nm-settled .ut-spade {
  opacity: 0.8;
  animation: none;
}

@keyframes ut-lower {
  0% {
    opacity: 0;
    transform: translateY(-13px);
  }
  70% {
    opacity: 1;
    transform: translateY(1.6px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes ut-plant {
  0% {
    opacity: 0;
    transform: translateY(-5px) rotate(-8deg);
  }
  100% {
    opacity: 1;
    transform: translateY(0) rotate(0);
  }
}

.nm-part-self.nm-undertaker svg {
  // the kit rests at the shoulder, leaning on the coin like a spade on a
  // headstone
  transform: translate(22%, 2%) rotate(10deg) scale(0.8);
}

// The information without the travel: every mark appears at rest.
@media (prefers-reduced-motion: reduce) {
  .nm *,
  .nm svg * {
    animation-duration: 0s !important;
    animation-delay: 0s !important;
  }
}
</style>
